import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { Heading, Text } from 'tio-ui/components/typography';
import { PhCaretRight } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import { getNavTokens } from 'tio-reimagined/color-tokens';
import type { NavColorTokens } from 'tio-reimagined/color-tokens';

// ---------------------------------------------------------------------------
// NavCard – unified navigation card with multiple layout variants
// ---------------------------------------------------------------------------

/**
 * Layout variant for the navigation card
 * - `vertical` — Icon centered on top, title below (quick action grid style)
 * - `horizontal` — Icon left, title/subtitle center, caret right (action row style)
 * - `gradient` — Vertical with gradient background (tool card style)
 */
export type NavCardLayout = 'vertical' | 'horizontal' | 'gradient';

/**
 * Color theme options for the navigation card
 */
export type NavCardColor =
  | 'violet'
  | 'indigo'
  | 'teal'
  | 'emerald'
  | 'blue'
  | 'amber'
  | 'pink'
  | 'green'
  | 'rose';

interface NavCardSignature {
  Args: {
    /** Route to navigate to */
    route: string;
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Optional subtitle text */
    subtitle?: string;
    /** Layout variant (default: 'vertical') */
    layout?: NavCardLayout;
    /** Color theme for the icon and hover effects */
    color?: NavCardColor;
  };
  Element: HTMLAnchorElement;
}

const colorClasses = {
  get violet() {
    return getNavTokens('violet');
  },
  get indigo() {
    return getNavTokens('indigo');
  },
  get teal() {
    return getNavTokens('teal');
  },
  get emerald() {
    return getNavTokens('emerald');
  },
  get blue() {
    return getNavTokens('blue');
  },
  get amber() {
    return getNavTokens('amber');
  },
  get pink() {
    return getNavTokens('pink');
  },
  get green() {
    return getNavTokens('green');
  },
  get rose() {
    return getNavTokens('rose');
  },
} satisfies Record<NavCardColor, NavColorTokens>;

/**
 * NavCard - A unified navigation card with layout variants
 *
 * Consolidates QuickActionLink (vertical), ActionLinkCard (horizontal),
 * and ToolCard (gradient) into a single component with a `@layout` arg.
 *
 * @example Vertical (quick action grid)
 * ```gts
 * import NavCard from 'tio-reimagined/components/nav-card';
 *
 * <NavCard @route="authenticated.explore" @icon={{PhCompass}} @title="Explore" @subtitle="150+ paths" @color="violet" />
 * ```
 *
 * @example Horizontal (action row)
 * ```gts
 * <NavCard @layout="horizontal" @route="authenticated.tas" @icon={{PhPlus}} @title="Start Application" @subtitle="Request funding" @color="indigo" />
 * ```
 *
 * @example Gradient (tool card)
 * ```gts
 * <NavCard @layout="gradient" @route="authenticated.coaching" @icon={{PhChatCircleDots}} @title="Loan Coaches" @subtitle="Expert guidance" @color="teal" />
 * ```
 */
export default class NavCard extends Component<NavCardSignature> {
  get layout() {
    return this.args.layout ?? 'vertical';
  }

  get color() {
    return this.args.color ?? 'violet';
  }

  get colors() {
    return colorClasses[this.color];
  }

  get isVertical() {
    return this.layout === 'vertical';
  }

  get isHorizontal() {
    return this.layout === 'horizontal';
  }

  get isGradient() {
    return this.layout === 'gradient';
  }

  <template>
    {{#if this.isHorizontal}}
      {{! Horizontal layout – icon left, text center, caret right }}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="horizontal"
        @route={{@route}}
        class="flex items-center gap-4 p-5 bg-white dark:bg-gray-900 rounded-xl shadow-sm ring-1 ring-gray-950/[0.04] dark:ring-gray-700 hover:shadow-md
          {{this.colors.hoverBorder}}
          transition-all group"
        ...attributes
      >
        <div
          data-test-nav-card-icon
          class="p-3 {{this.colors.bg}} rounded-xl {{this.colors.text}} transition-colors"
        >
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <div class="flex-1">
          <div data-test-nav-card-title class="font-semibold text-gray-900 dark:text-white">
            {{@title}}
          </div>
          {{#if @subtitle}}
            <Text data-test-nav-card-subtitle @intent="muted" class="text-sm">{{@subtitle}}</Text>
          {{/if}}
        </div>
        <PhCaretRight
          data-test-nav-card-caret
          @size={{20}}
          class="text-gray-400 dark:text-gray-500 {{this.colors.hoverCaret}}"
        />
      </LinkTo>
    {{else if this.isGradient}}
      {{! Gradient layout – vertical with gradient background }}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="gradient"
        @route={{@route}}
        class="flex flex-col items-center gap-2 p-4 bg-gradient-to-br
          {{this.colors.gradient}}
          rounded-xl hover:shadow-md transition-all border
          {{this.colors.gradientBorder}}
          text-center"
        ...attributes
      >
        <div data-test-nav-card-icon class="p-3 {{this.colors.bg}} rounded-xl">
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} class={{this.colors.text}} />
          {{/let}}
        </div>
        <span data-test-nav-card-title class="text-sm font-medium text-gray-900 dark:text-white">
          {{@title}}
        </span>
        {{#if @subtitle}}
          <span
            data-test-nav-card-subtitle
            class="text-xs text-gray-500 dark:text-gray-400"
          >{{@subtitle}}</span>
        {{/if}}
      </LinkTo>
    {{else}}
      {{! Vertical layout (default) – icon on top, text below }}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="vertical"
        @route={{@route}}
        class="bg-white dark:bg-gray-900 rounded-lg p-4 shadow-sm ring-1 ring-gray-950/[0.04] dark:ring-gray-700 hover:shadow-md
          {{this.colors.hoverBorder}}
          transition-all group text-center"
        ...attributes
      >
        <div
          data-test-nav-card-icon
          class="p-3
            {{this.colors.bg}}
            {{this.colors.text}}
            rounded-lg
            {{this.colors.hoverBg}}
            transition-colors mx-auto w-fit mb-2"
        >
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <Heading
          data-test-nav-card-title
          @level={{3}}
          class="text-sm font-semibold text-gray-900 dark:text-white"
        >{{@title}}</Heading>
        {{#if @subtitle}}
          <Text
            data-test-nav-card-subtitle
            @intent="muted"
            class="text-xs mt-1 hidden sm:block dark:text-gray-400"
          >{{@subtitle}}</Text>
        {{/if}}
      </LinkTo>
    {{/if}}
  </template>
}

export type { NavCardSignature };
